import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, Clock } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, loginResponse, clearError, clearLoginResponse } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    clearLoginResponse();
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    }
  };

  const renderAccountStatus = () => {
    if (!loginResponse) return null;

    const statusConfig = {
      pending: {
        icon: <Clock className="w-12 h-12 text-amber-500" />,
        title: 'Cuenta en Revisión',
        message: loginResponse.message || 'Tu cuenta está pendiente de aprobación.',
        color: 'amber'
      },
      rejected: {
        icon: <AlertCircle className="w-12 h-12 text-red-500" />,
        title: 'Cuenta Rechazada',
        message: loginResponse.message || 'Tu cuenta ha sido rechazada.',
        detail: loginResponse.rejection_reason,
        color: 'red'
      },
      blocked: {
        icon: <AlertCircle className="w-12 h-12 text-red-500" />,
        title: 'Cuenta Bloqueada',
        message: loginResponse.message || 'Tu cuenta ha sido bloqueada.',
        detail: loginResponse.blocked_reason,
        color: 'red'
      }
    };

    const config = statusConfig[loginResponse.status];
    if (!config) return null;

    return (
      <div className={`bg-${config.color}-50 border border-${config.color}-200 rounded-2xl p-6 mb-6`}>
        <div className="flex flex-col items-center text-center">
          {config.icon}
          <h3 className={`text-lg font-semibold text-${config.color}-800 mt-3`}>{config.title}</h3>
          <p className={`text-${config.color}-600 mt-2`}>{config.message}</p>
          {config.detail && (
            <p className={`text-${config.color}-500 text-sm mt-2 italic`}>
              Razón: {config.detail}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 pt-12 pb-20 px-6">
        <button
          onClick={() => navigate('/welcome')}
          className="flex items-center text-white/80 hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver
        </button>
        <h1 className="text-3xl font-bold text-white">Bienvenido</h1>
        <p className="text-teal-100 mt-2">Inicia sesión para continuar</p>
      </div>

      {/* Form Card */}
      <div className="px-6 -mt-12">
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6">
          {renderAccountStatus()}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Iniciando...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-teal-600 font-semibold hover:text-teal-700">
                Regístrate
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
